/**
 * Motion, derived from the app's springs rather than eyeballed as curves.
 *
 * design-language.md §3 says "springs, not curves": state changes use
 * `withSpring` with damping 14–18, because a fixed-duration easing reads as
 * choreographed and a spring reads as a thing settling. The web has no spring
 * runtime and does not need one — CSS `linear()` takes an arbitrary sampled
 * curve, so the honest port is to SOLVE the same spring the app runs and hand
 * the browser its samples. The motion is imported, not re-implemented, exactly
 * as the eigenfield's clock is.
 *
 * The parameters below are Reanimated's `withSpring` defaults (mass 1,
 * stiffness 100) at the damping the design language names. Change the damping
 * here and the web's easing and the app's springs stay the same motion; write a
 * `cubic-bezier` at a call site and they immediately are not.
 */

export interface SpringSpec {
  /** Reanimated's `damping`. 14–18 per design-language.md §3. */
  damping: number;
  stiffness: number;
  mass: number;
}

/** The one spring. Mid-range damping: enough overshoot to read as physical,
 *  not enough to read as a bounce. */
export const SPRING: SpringSpec = { damping: 16, stiffness: 100, mass: 1 };

/**
 * Normalised displacement of a spring released from 1, at time `t` seconds.
 * Returns 1 at t = 0 and decays to 0. Handles both the underdamped case
 * (ζ < 1, the interesting one) and the critically/over-damped case, because a
 * caller is free to pass damping outside the design range and getting NaN out
 * of `sqrt` of a negative would be a silent failure.
 */
export function springDisplacement(t: number, spec: SpringSpec = SPRING): number {
  const { damping: c, stiffness: k, mass: m } = spec;
  const w0 = Math.sqrt(k / m);
  const zeta = c / (2 * Math.sqrt(k * m));
  if (zeta < 1) {
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    return Math.exp(-zeta * w0 * t) * (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t));
  }
  // Critically damped (ζ = 1) is the limit of the overdamped form; both are
  // covered by the same expression at ζ = 1 without a separate branch.
  const wd = w0 * Math.sqrt(zeta * zeta - 1);
  if (wd === 0) return Math.exp(-w0 * t) * (1 + w0 * t);
  return (
    Math.exp(-zeta * w0 * t) * (Math.cosh(wd * t) + ((zeta * w0) / wd) * Math.sinh(wd * t))
  );
}

/**
 * The spring's full state at time `t`, released from ANY displacement `x0`
 * with ANY velocity `v0` — the general solution `springDisplacement` is the
 * special case of (x0 = 1, v0 = 0).
 *
 * The CSS easing above can only ever start at rest, which is exactly right for
 * a transition and exactly wrong for anything a hand is holding: a thumb that
 * is flicked, a value that is retargeted halfway through its travel. Those
 * need the motion to CONTINUE from where and how fast the thing is moving, or
 * it visibly stops and restarts. That is the only job this has; everything
 * that can be a CSS transition stays one.
 *
 * Closed form rather than stepped, so the result is exact at any frame rate
 * and a dropped frame can never inject energy. `x` is displacement FROM the
 * target (0 = at rest on it) in whatever unit the caller animates; `v` is its
 * rate of change per second.
 */
export function springAt(
  t: number,
  x0: number,
  v0: number,
  spec: SpringSpec = SPRING,
): { x: number; v: number } {
  const { damping: c, stiffness: k, mass: m } = spec;
  const w0 = Math.sqrt(k / m);
  const zeta = c / (2 * Math.sqrt(k * m));
  if (zeta < 1) {
    const a = zeta * w0;
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    const e = Math.exp(-a * t);
    const cos = Math.cos(wd * t);
    const sin = Math.sin(wd * t);
    return {
      x: e * (x0 * cos + ((v0 + a * x0) / wd) * sin),
      v: e * (v0 * cos - ((a * v0 + w0 * w0 * x0) / wd) * sin),
    };
  }
  if (zeta === 1) {
    const e = Math.exp(-w0 * t);
    const b = v0 + w0 * x0;
    return { x: e * (x0 + b * t), v: e * (v0 - w0 * b * t) };
  }
  // Overdamped: two real roots, both negative.
  const root = w0 * Math.sqrt(zeta * zeta - 1);
  const r1 = -zeta * w0 + root;
  const r2 = -zeta * w0 - root;
  const a1 = (v0 - r2 * x0) / (r1 - r2);
  const a2 = x0 - a1;
  const e1 = Math.exp(r1 * t);
  const e2 = Math.exp(r2 * t);
  return { x: a1 * e1 + a2 * e2, v: r1 * a1 * e1 + r2 * a2 * e2 };
}

/**
 * Seconds until the spring is within `epsilon` of rest and stays there.
 *
 * `epsilon` is 1%, not the 0.1% a physics settle test would use, and that is a
 * UI decision rather than a sloppy one: the last 1% of a 14px translate is a
 * tenth of a pixel, so a duration that waits for it (863ms at 0.1%, vs 576ms
 * here) spends a third of a second animating something nobody can see, and
 * every transition on the site inherits it.
 */
export function springSettleSeconds(spec: SpringSpec = SPRING, epsilon = 0.01): number {
  const { damping: c, stiffness: k, mass: m } = spec;
  const zeta = c / (2 * Math.sqrt(k * m));
  const w0 = Math.sqrt(k / m);
  // The envelope is e^(-ζω₀t); solve it for epsilon. Exact for the decay term,
  // and the oscillation inside it is bounded by that envelope, so this is an
  // upper bound rather than an approximation that can come out short.
  return -Math.log(epsilon) / (zeta * w0);
}

/**
 * The spring as a CSS `linear()` easing.
 *
 * `linear()` interpolates between evenly spaced samples, so the sample count is
 * the whole accuracy story: too few and the overshoot is clipped into a
 * straight line, which is precisely the "choreographed" look the spring exists
 * to avoid. 32 is comfortably past the point where more samples change the
 * rendered curve.
 */
export function springEasing(spec: SpringSpec = SPRING, samples = 32): string {
  const settle = springSettleSeconds(spec);
  const points: string[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = (i / samples) * settle;
    const progress = 1 - springDisplacement(t, spec);
    // 4dp: enough to carry the overshoot, short enough that the emitted token
    // stays readable in the generated stylesheet.
    points.push(Number(progress.toFixed(4)).toString());
  }
  // Land exactly at rest. The analytic curve is still ~1.008 at the 1% settle
  // point; leaving that in means every transition finishes a hair past its
  // target and snaps back on the next frame.
  points[points.length - 1] = '1';
  return `linear(${points.join(', ')})`;
}

/**
 * Offset between two elements that change together, in milliseconds.
 *
 * design-language.md §3: "Simultaneous means a screen refreshed; staggered
 * means this caused that." 60–80ms is the stated range; 70 is its middle, and
 * having one constant rather than a per-surface number is what stops the range
 * from drifting into 52 here and 90 there.
 */
export const STAGGER_MS = 70;

/** How many elements deep a stagger may go before it stops adding delay. Past
 *  this the last item is waiting on choreography rather than reading as
 *  consequence. */
export const STAGGER_MAX_STEPS = 12;

/** Duration for motion that is NOT a spring — a scrim fading, a colour
 *  changing. Short enough not to be a transition anyone waits through. */
export const FADE_MS = 180;
